import { forwardRef, useState } from 'react'
import { Input, type InputProps } from './Input'
import { IconButton } from './IconButton'
import { Eye, EyeOff } from '@/components/icons'
import { cn } from '@/lib/cn'

/** Password field with an optional strength meter. */

export interface PasswordInputProps extends Omit<InputProps, 'type' | 'trailing'> {
  /** Show the strength meter and rule list. Off on the login form — nobody */
  showStrength?: boolean
}

export const MIN_PASSWORD_LENGTH = 12

/** A deliberately small blocklist for immediate client-side feedback. The real */
const OBVIOUS = [
  'password', 'paisa24', 'barota', '123456', 'qwerty', 'admin', 'welcome',
  'letmein', 'iloveyou', 'abc123', 'password123', 'admin123',
]

export type Strength = 'weak' | 'fair' | 'good' | 'strong'

/** Named keys, not a Record — `noUncheckedIndexedAccess` would otherwise widen */
export interface PasswordRules {
  length: boolean
  notCommon: boolean
}

export function scorePassword(value: string): { strength: Strength; meets: PasswordRules } {
  const lower = value.toLowerCase()
  const meets: PasswordRules = {
    length: value.length >= MIN_PASSWORD_LENGTH,
    notCommon: value.length > 0 && !OBVIOUS.some((w) => lower.includes(w)),
  }

  if (!meets.length || !meets.notCommon) return { strength: 'weak', meets }

  // Length is the dominant factor in real-world strength, so it is what.
  const variety = [/[a-z]/, /[A-Z]/, /\d/, /[^a-zA-Z0-9]/].filter((r) => r.test(value)).length
  if (value.length >= 20 || (value.length >= 16 && variety >= 3)) return { strength: 'strong', meets }
  if (value.length >= 14 || variety >= 3) return { strength: 'good', meets }
  return { strength: 'fair', meets }
}

const METER: Record<Strength, { width: string; tone: string; label: string }> = {
  weak:   { width: 'w-1/4', tone: 'bg-danger',  label: 'Weak' },
  fair:   { width: 'w-2/4', tone: 'bg-warning', label: 'Fair' },
  good:   { width: 'w-3/4', tone: 'bg-primary', label: 'Good' },
  strong: { width: 'w-full', tone: 'bg-success', label: 'Strong' },
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ showStrength = false, value, hint, ...props }, ref) {
    const [visible, setVisible] = useState(false)
    const text = typeof value === 'string' ? value : ''
    const { strength, meets } = scorePassword(text)
    const meter = METER[strength]

    return (
      <div className="flex flex-col gap-1.5">
        <Input
          ref={ref}
          type={visible ? 'text' : 'password'}
          value={value}
          hint={showStrength ? undefined : hint}
          trailing={
            <IconButton
              icon={visible ? EyeOff : Eye}
              label={visible ? 'Hide password' : 'Show password'}
              size="sm"
              // Toggling visibility must not submit the form it sits in.
              type="button"
              onClick={() => { setVisible((v) => !v) }}
            />
          }
          {...props}
        />

        {showStrength && (
          <>
            <div className="flex items-center gap-2">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-neutral-bg">
                <div
                  className={cn('h-full rounded-full transition-all duration-200',
                    text ? meter.width : 'w-0', meter.tone)}
                />
              </div>
              <span className="w-12 text-xs text-subtle">{text ? meter.label : ''}</span>
            </div>

            <ul className="flex flex-col gap-0.5 text-xs text-subtle">
              <Rule met={meets.length}>At least {MIN_PASSWORD_LENGTH} characters</Rule>
              <Rule met={meets.notCommon}>Not a common password</Rule>
            </ul>
          </>
        )}
      </div>
    )
  },
)

function Rule({ met, children }: { met: boolean; children: React.ReactNode }) {
  return (
    <li className={cn('flex items-center gap-1.5', met && 'text-success')}>
      <span
        aria-hidden
        className={cn('inline-block h-1 w-1 rounded-full',
          met ? 'bg-success' : 'bg-neutral-border')}
      />
      {children}
    </li>
  )
}
